import { AccountRunStatus } from '@hit-game/shared/enums';
import type { IAccountDataResponse } from '@hit-game/shared/types';
import { motion } from 'framer-motion';
import { AlertTriangle, Clock, Settings2 } from 'lucide-react';

import { formatMoneyOrUnknown } from '../../utils/format';
import { AccountCardActions, type AviatorCardControls } from './AccountCardActions';
import { AccountCardMeta } from './AccountCardMeta';

export interface AccountCardActions {
  onLogin: (id: string) => Promise<void>;
  onLogout: (id: string) => Promise<void>;
  onRefreshBalance: (id: string) => Promise<void>;
  onShowBetLogs: (account: IAccountDataResponse) => void;
  onShowTransactions: (account: IAccountDataResponse) => void;
  onShowDeposit: (account: IAccountDataResponse) => void;
  onShowInfo: (account: IAccountDataResponse) => void;
  onDelete: (account: IAccountDataResponse) => void;
}

interface AccountCardProps extends AccountCardActions {
  account: IAccountDataResponse;
  /** Thẻ nằm trong nhóm: bỏ nút đăng nhập/đăng xuất lẻ, việc đó làm ở cấp nhóm. */
  inGroup?: boolean;
  /** Màu tên tài khoản — nhóm tô mỗi thành viên một màu để đối chiếu nhanh. */
  headerColor?: string;
  /** Hàng điều khiển Máy bay. Bỏ trống = không hiện — xem `AccountCardActions`. */
  aviator?: AviatorCardControls;
}

/**
 * Thẻ một tài khoản ngoài sảnh.
 *
 * Chỉ còn phần khung + đầu thẻ (tên, trạng thái, số dư, token) cho GỌN — nạp/rút/
 * lãi lỗ, cấu hình, số ván, proxy gập trong `AccountCardMeta` (bấm "Chi tiết" mới
 * xổ ra), hàng nút ở `AccountCardActions`. Trước đây cả ba gộp trong một hàm 265 dòng, mà mỗi
 * phần lại giữ state riêng (`busy`, `copied`) nên đọc tới đâu cũng phải nhớ
 * ngược lên đầu.
 */
export function AccountCard({
  account,
  inGroup = false,
  headerColor,
  aviator,
  ...actions
}: AccountCardProps) {
  const running = account.status === AccountRunStatus.RUNNING;

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border-line text-ink bg-panel-raised flex flex-col rounded-xl border shadow-sm"
    >
      {/* ── Tên + trạng thái ─────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-2 px-4 pb-3 pt-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <div
              className="truncate text-sm font-bold"
              title={account.username}
              style={headerColor ? { color: headerColor } : undefined}
            >
              {account.username}
            </div>
            {/* Cùng chỗ với nút Sửa của thẻ nhóm: cấu hình là thuộc tính của THẺ,
                không phải một thao tác ngang hàng với Start/Stop ở hàng nút dưới.
                Đang chạy vẫn sửa được: bản mới áp dụng từ VÁN SAU (ván đang đặt/đang
                bay giữ số cũ). */}
            {aviator && (
              <button
                type="button"
                onClick={() => {
                  aviator.onConfig(account);
                }}
                className="text-ink-dim hover:bg-panel-hover hover:text-accent shrink-0 rounded p-1 transition-colors"
                title={
                  aviator.running
                    ? 'Cấu hình Máy bay — đang chạy, bản mới áp dụng từ ván sau'
                    : 'Cấu hình Máy bay — tiền cược và mức tự dừng'
                }
              >
                <Settings2 size={13} />
              </button>
            )}
          </div>
          <div className="text-ink-muted mt-0.5 truncate text-[11px]" title={account.fullname}>
            {account.fullname || '—'}
          </div>
          {/* Proxy chết thì mọi thao tác với nhà cái (đăng nhập, số dư, đăng xuất, đặt cược) đều
              hỏng hoặc treo — phải thấy ngay trên thẻ, không phải mở "Chi tiết" mới biết. */}
          {account.proxyAlive === false && (
            <div
              className="bg-danger-bg text-danger mt-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase"
              title={`Proxy không phản hồi: ${account.proxy} — đổi proxy ở trang Proxy`}
            >
              <AlertTriangle size={9} />
              Proxy chết
            </div>
          )}
        </div>

        <div
          className={`shrink-0 whitespace-nowrap rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${
            running ? 'text-success bg-success/10' : 'text-ink-dim bg-ink-dim/10'
          }`}
        >
          {running && (
            <motion.span
              animate={{ opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="bg-success mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle"
            />
          )}
          {running ? 'Đang chạy' : 'Đã dừng'}
        </div>
      </div>

      {/* ── Số dư + token ─────────────────────────────────────────── */}
      <div className="grid grid-cols-2 overflow-hidden px-4 pb-4">
        <div className="flex min-w-0 flex-col items-start gap-1">
          <span className="text-ink-dim text-[11px] font-medium uppercase tracking-[0.08em]">
            Số dư
          </span>
          <span className="text-ink w-full truncate font-mono text-[22px] font-bold leading-none">
            {formatMoneyOrUnknown(account.balance)}
          </span>
        </div>
        <div className="border-line flex min-w-0 flex-col items-start gap-1 border-l pl-4">
          <span className="text-ink-dim text-[11px] font-medium uppercase tracking-[0.08em]">
            Token
          </span>
          <span
            className={`flex w-full items-center gap-1 truncate font-mono text-[18px] font-bold leading-none ${
              account.tokenWarning ? 'text-warning' : 'text-ink'
            }`}
            title={account.tokenTime}
          >
            {account.tokenWarning && <Clock className="text-warning h-3.5 w-3.5 shrink-0" />}
            <span className="min-w-0 truncate">{account.tokenTime}</span>
          </span>
        </div>
      </div>

      <AccountCardMeta account={account} />

      <AccountCardActions
        account={account}
        showSoloActions={!inGroup}
        {...(aviator ? { aviator } : {})}
        {...actions}
      />
    </motion.div>
  );
}
